// UpNextCard — phase-aware dominant card on the program dashboard.
//
// One slot, four shapes:
//   1. "Build your team" — student is registered but has no team yet
//   2. "Up next: <deliverable>" — at least one deliverable left to submit
//   3. "Judges are reviewing" — every deliverable is in (or comp is "judging")
//   4. "Final results" — comp is closed and the team has a placement
//
// Lives at the top of the dashboard's actionable column (visually under the
// at-a-glance band). Forest background to anchor the page.



import { HeroCard } from "../../aspects/HeroCard";
import { PANE_THEMES } from "../../brand/paneThemes";

const STUDENT_THEME = PANE_THEMES.Student;

import type {
  Deliverable,
  LeaderboardRow,
  TeamSubmissionRow,
} from "../../../types/database";


// Card-local status union: the central DB CompetitionStatus plus the
// UI-synthesized "closed_to_registration" (open competition past its
// registration deadline but before judging). Pre-active states like
// "submitted" / "approved" never reach this card in practice — students
// only see their registered programs — but we accept them to stay
// assignable from the wide DB type and fall through to default copy.
import type { CompetitionStatus as DbCompetitionStatus } from "../../../types/database";
export type CompetitionStatus = DbCompetitionStatus | "closed_to_registration";

export type UpNextCardProps = {
  /** Status of the competition (drives the "judging" / "closed" branches). */
  status: CompetitionStatus;
  /** Whether the student is on a team. */
  hasTeam: boolean;
  /** All deliverables for this comp (used to find the next unsubmitted one). */
  deliverables: Deliverable[];
  /** Submissions for the student's team (one per deliverable id, at most). */
  submissions: TeamSubmissionRow[];
  /** Leaderboard row for the student's own team — drives the closed-state recap. */
  ownTeamRow?: LeaderboardRow | null;
  /** ISO close date — used in the "judging" caption ("results around X"). */
  closesAt?: string | null;
  /** Number of teams in the competition (denominator on "ranked X of N"). */
  teamCount?: number;

  onPressFindTeam: () => void;
  onPressDeliverable: (deliverableId: string) => void;
  onPressLeaderboard: () => void;
};

function fmtMonthDay(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function daysBetween(targetIso: string | null | undefined, now: number = Date.now()): number | null {
  if (!targetIso) return null;
  const t = new Date(targetIso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - now) / (24 * 60 * 60 * 1000));
}

export function UpNextCard(props: UpNextCardProps) {
  const {
    status,
    hasTeam,
    deliverables,
    submissions,
    ownTeamRow,
    closesAt,
    teamCount,
    onPressFindTeam,
    onPressDeliverable,
    onPressLeaderboard,
  } = props;

  // ---- Phase 1: no team yet (when comp is still open for registration). ----
  if (!hasTeam && (status === "open" || status === "draft")) {
    return (
      <HeroCard theme={STUDENT_THEME}
        overline="Get started"
        title="Build your team"
        subtitle="Find a public team to join, accept an invite, or create your own. Most deliverables require teamwork."
        ctaLabel="Find a team"
        onPress={onPressFindTeam}
      />
    );
  }

  // ---- Phase 4: closed — show final placement if we have it. ----
  if (status === "closed") {
    if (ownTeamRow && ownTeamRow.placement != null) {
      const denom = teamCount && teamCount > 0 ? " of " + teamCount : "";
      return (
        <HeroCard theme={STUDENT_THEME}
          overline="Final result"
          title={"Ranked #" + ownTeamRow.placement + denom}
          subtitle={
            ownTeamRow.final_score != null
              ? "Final score " + ownTeamRow.final_score.toFixed(2) + " · view the full leaderboard for the breakdown."
              : "View the full leaderboard for the breakdown."
          }
          ctaLabel="View leaderboard"
          onPress={onPressLeaderboard}
        />
      );
    }
    return (
      <HeroCard theme={STUDENT_THEME}
        overline="Closed"
        title="Competition wrapped"
        subtitle="Thanks for participating. Check the leaderboard for final standings."
        ctaLabel="View leaderboard"
        onPress={onPressLeaderboard}
      />
    );
  }

  // ---- Phase 3: judging in progress. ----
  if (status === "judging") {
    return (
      <HeroCard theme={STUDENT_THEME}
        overline="In review"
        title="Judges are reviewing submissions"
        subtitle={
          closesAt
            ? "Results expected around " + fmtMonthDay(closesAt) + ". You'll see the live leaderboard fill in as scores come back."
            : "You'll see the live leaderboard fill in as scores come back."
        }
        ctaLabel="View leaderboard"
        onPress={onPressLeaderboard}
      />
    );
  }

  // ---- Phase 2: at least one deliverable left to submit. ----
  const submittedIds = new Set(submissions.map((s) => s.deliverable_id));
  const sorted = [...deliverables].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0));
  const next = sorted.find((d) => !submittedIds.has(d.id));

  if (!next) {
    // All submitted but comp not yet flagged as judging — bridge state.
    return (
      <HeroCard theme={STUDENT_THEME}
        overline="All submitted"
        title="Nothing left to turn in"
        subtitle="Sit tight — judges will be along shortly. The leaderboard fills in as scores come back."
        ctaLabel="View leaderboard"
        onPress={onPressLeaderboard}
      />
    );
  }

  const days = daysBetween(next.due_at);
  const isLocked = next.opens_at != null && new Date(next.opens_at).getTime() > Date.now();
  const dueLine = (() => {
    if (!next.due_at) return "Due date not set yet";
    if (isLocked) return "Unlocks " + fmtMonthDay(next.opens_at) + " · due " + fmtMonthDay(next.due_at);
    if (days == null) return "Due " + fmtMonthDay(next.due_at);
    if (days < 0) return "Past due (" + Math.abs(days) + (Math.abs(days) === 1 ? " day" : " days") + ")";
    if (days === 0) return "Due today";
    if (days === 1) return "Due tomorrow";
    return "Due in " + days + " days";
  })();
  const points =
    typeof next.point_value === "number" && next.point_value > 0
      ? " · " + next.point_value + " pts"
      : "";

  return (
    <HeroCard theme={STUDENT_THEME}
      overline="Up next"
      title={next.name}
      subtitle={dueLine + points}
      countdown={days != null && days >= 0 ? { days, urgent: days <= 3 } : null}
      ctaLabel={isLocked ? "Preview" : "Open submissions"}
      onPress={() => onPressDeliverable(next.id)}
    />
  );
}

